const yup = require('yup')
const { requiredFields, labelify } = require('./helpers')
const { uploadSchema } = require('./cloudinary-schema')
const { uploadSchema: s3UploadSchema } = require('./s3-schema')
const { isEmpty } = require('ramda')
const usStateUtils = require('../usa-states')

const { findStateByShortcode } = usStateUtils

const statuses = ['draft', 'pending', 'processing', 'shipped', 'cancelled', 'failed']
const acStatuses = ['Pending', 'Received', 'Production', 'QC', 'Shipped', 'Cancelled']
const shippingLevels = ['cp_ground', 'cp_fast']

const orderItemSchema = yup.object().shape({
  key: yup.string().default(() => Math.random().toString(36).slice(2, 11)),
  product: yup.mixed().required().default(''),
  quantity: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
    .min(1, ({ min }) => `Quantity must be at least ${min}`)
    .max(500, ({ max }) => `Quantity cannot exceed ${max}`)
    .default(0),
  staple: yup.bool().default(false),
  flat: yup.bool().default(false),
  shortEdge: yup.number().default(0),
  longEdge: yup.number().default(0),
  notes: yup.string().max(150).nullable(),
  title: yup.string().max(50).nullable(),
  document: uploadSchema,
  attachment: s3UploadSchema,
  printFile: s3UploadSchema,
  fileMeta: yup.object().shape({
    pageWidth: yup.number(),
    pageHeight: yup.number(),
    pageCount: yup.number(),
    orientation: yup.string(),
  }),
})

const shape = labelify({
  status: yup.string().oneOf(statuses).default('draft'),
  shippingLevel: yup.string().oneOf(shippingLevels).default('cp_ground'),
  orderItems: yup.array().of(orderItemSchema).default([orderItemSchema.cast()]),
  pricing: yup
    .object()
    .shape({
      grandTotal: yup.number(),
      totalFees: yup.number(),
      total: yup.number(),
      fees: yup.array().of(yup.object().shape({ name: yup.string(), amount: yup.number(), note: yup.string() })),
      items: yup.array().of(
        yup.object().shape({
          id: yup.string(),
          key: yup.string(),
          quantity: yup.number(),
          unitCost: yup.number(),
          subtotal: yup.number(),
          sku: yup.string(),
        })
      ),
    })
    .when('status', {
      is: 'pending',
      then: (schema) =>
        schema
          .required('Pricing is required')
          .test('not-empty', 'Pricing details could not be calculated. Please, try again later.', (value) => !isEmpty(Object.values(value).filter(Boolean)))
          .test('grand-total', 'Total cost should be more than 0', (value) => Number(value?.grandTotal) > 0),
      otherwise: (schema) => schema.optional(),
    }),
  address: yup
    .object({
      street1: yup.string(),
      street2: yup.string(),
      city: yup.string(),
      state: yup.string(),
      zip: yup.string(),
    })
    .when('status', {
      is: 'pending',
      then: (schema) =>
        schema
          .required('Address is required')
          .test('not-empty', 'Address is required to submit the print order.', (value) => !isEmpty(Object.values(value).filter(Boolean)))
          .test('state-required', 'State is required.', (value) => value?.state && value.state.trim() !== '')
          .test(
            'state-valid',
            'State must be a valid two-character US state code (e.g., CA, NY, TX).',
            (value) => value?.state && findStateByShortcode(value.state)
          ),
      otherwise: (schema) => schema.notRequired(),
    })
    .optional(),
  name: yup.object().shape({
    first: yup.string().ensure(),
    last: yup.string().ensure(),
  }),
  email: yup
    .string()
    .email()
    .when('status', {
      is: 'pending',
      then: (s) => s.required(),
    })
    .ensure(),
  phone: yup
    .string()
    .when('status', {
      is: 'pending',
      then: (s) => s.required(),
    })
    .ensure(),
  shipmentTrackingEmails: yup.array().of(yup.string().email('Shipment tracking email must be valid.')).default([]),
  trackingNumbers: yup.array().of(yup.string()).default([]),
  expectedShipDateEstimate: yup.string(),
  acOrderStatus: yup.string().oneOf(acStatuses),
  acOrderNumber: yup.string().optional(),
  providerOrderRef: yup.string().optional(),
  providerOrderStatus: yup.string().optional(),
  externalOrder: yup.object().shape({
    reference: yup.string(),
    status: yup.object().shape({
      label: yup.string(),
      code: yup.number(),
    }),
    isCancelable: yup.boolean(),
    date: yup.date(),
  }),
  cancelledAt: yup.date(),
  cancelAttempts: yup.number().default(0),
  submitAttempts: yup.number().default(0),
  createdAt: yup.date(),
  updatedAt: yup.date(),
  createdBy: yup.mixed(),
  updatedBy: yup.mixed(),
})

const printOrderSchemaPartial = yup.object().shape(shape)
const printOrderSchema = printOrderSchemaPartial.shape(requiredFields(['name'])(shape))

module.exports = { printOrderSchema, printOrderSchemaPartial }
